import React from 'react';
import styled, { css } from 'styled-components';
import { withCssSelector } from '../../shared/withCssSelector';

export const radioSizes = {
	large: 'large',
	compact: 'compact',
	min: 'min',
} as const;

export type RadioSize = typeof radioSizes[keyof typeof radioSizes];

/* eslint-disable no-magic-numbers */
const circleSize = 20;
const dotSize = 10;
const dotPressedSize = 14;
/* eslint-enable no-magic-numbers */

const StyledInput = styled.input<{
	$disabled?: boolean | undefined;
	$hasError?: boolean | undefined;
	$size: RadioSize;
}>`
	box-sizing: border-box;
	position: relative;
	width: ${circleSize}px;
	height: ${circleSize}px;
	padding: 0;
	border-radius: 50%;
	pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
	border-style: solid;
	border-width: ${props => props.theme.form.radio.stroke.rested}px;
	border-color: ${props => props.theme.form.radio.default.border};
	appearance: none;
	background-color: ${props => props.theme.form.radio.default.background};
	outline: none !important;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	// Click target. Vertical only — the row grows above and below the circle while
	// the horizontal reach comes from the <label> wrapping it, which is already
	// clickable. Sized off the centre so the border width never eats into it.
	// ponytail: overlaps rows packed closer than the target — space rows by the
	// target size, or wrap in a sized box if that ever bites.
	&::after {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: ${circleSize}px;
		height: ${props => props.theme.form.radio.target[props.$size]}px;
	}

	&:hover {
		border-color: ${props => props.theme.form.radio.hover.border};
	}

	&:active {
		border-width: ${props => props.theme.form.radio.stroke.pressed}px;
		border-color: ${props => props.theme.form.radio.hover.border};
	}

	// Keyboard focus only — a mouse press already shows the pressed stroke.
	&:focus-visible {
		outline: none;
		border-color: ${props => props.theme.form.radio.focus.border};
	}

	// The filled disc is the whole control; the dot is a hole punched out of it,
	// so the border is painted in the fill colour rather than removed — that keeps
	// the drawn size identical between checked and unchecked.
	&:checked {
		border-color: ${props => props.theme.form.radio.checked.border};
		background: ${props => props.theme.form.radio.checked.background};

		&:before {
			content: '';
			width: ${dotSize}px;
			height: ${dotSize}px;
			border-radius: 50%;
			background-color: ${props => props.theme.form.radio.checked.dot};
		}

		// The dot dims with the disc it sits on, keeping their contrast steady.
		&:hover:before,
		&:active:before {
			background-color: ${props => props.theme.form.radio.checked.engagedDot};
		}

		// Pressed grows the dot rather than dimming the disc, thinning the ring.
		&:active:before {
			width: ${dotPressedSize}px;
			height: ${dotPressedSize}px;
		}
	}

	${props => props.$hasError && css`
		border-color: ${props.theme.form.input.error.border};
		box-shadow: ${props.theme.form.input.error.shadow};
	`}

	${({ $disabled, theme }) => $disabled && css`
		opacity: ${theme.form.disabled.opacity};
	`}
`;

export type RadioProps = {
	hasError?: boolean | undefined,
	size?: RadioSize | undefined
} & Omit<React.ComponentPropsWithoutRef<'input'>, 'type' | 'size'>

export const Radio = withCssSelector(React.forwardRef<HTMLInputElement, RadioProps>(
	function Radio(
		{
			hasError,
			disabled,
			tabIndex,
			readOnly,
			size = radioSizes.compact,
			...props
		},
		ref
	) {
		return <StyledInput
			ref={ref}
			type={'radio'}
			$hasError={hasError}
			$disabled={disabled}
			$size={size}
			tabIndex={disabled ? -1 : tabIndex}
			readOnly={disabled ? true : readOnly}
			{...props}
		/>;
	}
), StyledInput.toString());
